import { create } from 'zustand';
// 세션이 끝나면 모듈 캐시도 함께 비운다 (2026-08-31) — 앞 사람의 직위·소속이 남던 일.
import { clearSessionCaches } from '../lib/session-caches';
import { useScreenLockStore } from './screenLockStore';
import { persist } from 'zustand/middleware';

export interface AuthUser {
  id: number;
  email: string;
  name: string;
  role: 'STAFF' | 'MANAGER' | 'ADMIN' | 'SYSTEM_ADMIN';
  institutionId: number;
  // 프로필 이미지 — 외부 URL(https?://) 또는 업로드 참조(wcfile:{fileId}). NavBar 아바타 등 전화면 표시용.
  // 부팅 시 /users/me probe 가 채움(design-20260606 §17.1). 미설정 시 이니셜 폴백.
  avatarUrl?: string | null;
}

/**
 * 인증 상태 머신 (auth v1.5 cookie cutover — TD-5).
 *
 *   pending : 초기/새로고침 직후 — AuthGuard 가 /users/me probe 후 결정 전
 *   authed  : probe 200 또는 login 성공 직후 — children 렌더 가능
 *   anon    : probe 401 또는 logout — /login 으로 redirect
 *
 * 토큰은 store 가 보유하지 않음. backend httpOnly cookie (wc_at, wc_rt) 가 SSOT.
 * frontend 의 axios `withCredentials:true` 가 cookie 를 자동 동봉.
 */
export type AuthStatus = 'pending' | 'authed' | 'anon';

interface AuthState {
  user: AuthUser | null;
  authStatus: AuthStatus;
  /** authStatus==='authed' 와 동치 — 기존 isAuthenticated 호출자 호환. set 시 같이 sync. */
  isAuthenticated: boolean;
  setSession: (user: AuthUser) => void;
  setAuthStatus: (status: AuthStatus) => void;
  clearSession: () => void;
}

/**
 * BroadcastChannel cross-tab sync (auth v1.5 — TD-10).
 *
 * payload 변경 (Pre-mortem S3 봉합):
 *   - `session-cleared`        : logout / probe 401 — 모든 탭 정리
 *   - `tokens-refreshed-ok`    : refresh 성공 통지 (no payload — cookie jar 가 자동 공유)
 *   - `auth-probe-result`      : 한 탭의 /users/me probe 결과 broadcast — 다른 탭이 즉시 활용
 *                                (cold-start probe 폭주 봉큐)
 *
 * 이전(v1.4) 의 `tokens-updated` (access/refresh payload) 는 제거 — cookie 모드에선 불필요.
 *
 * Cycle 22 — storage event fallback (BroadcastChannel 미지원 브라우저):
 *   - localStorage key 'workchat:auth-bcast' 에 timestamp + msg 쓰기 → 다른 탭 storage event 수신
 *   - BroadcastChannel 지원 시에도 함께 발신 (양쪽 모두) — 중복 처리 회피 위해 msg id 기반 dedupe
 *
 * Cycle 22 — user-mismatch reload:
 *   - 다른 탭에서 다른 user.id 로 login 시 본 탭이 force reload (stale UI 회피)
 *
 * SSR 가드: window 미존재 환경 (build) 에서 BroadcastChannel 생성 안 함.
 */
type BroadcastMsg =
  | { type: 'session-cleared' }
  | { type: 'tokens-refreshed-ok' }
  | { type: 'auth-probe-result'; user: AuthUser | null };

interface BroadcastEnvelope {
  id: string;
  msg: BroadcastMsg;
  ts: number;
}

const CHANNEL_NAME = 'workchat:auth';
const STORAGE_KEY = 'workchat:auth-bcast';
const isBrowser = typeof window !== 'undefined';
const hasBroadcastChannel = isBrowser && typeof BroadcastChannel !== 'undefined';
const channel: BroadcastChannel | null = hasBroadcastChannel ? new BroadcastChannel(CHANNEL_NAME) : null;

// 중복 처리 회피 — 본 탭이 보낸 메시지가 storage event 로 다시 받아질 수 있으므로 id 기록.
// Cycle 23 — LRU: 최대 SEEN_MAX 개 + 10분 너머 자동 만료. process 평생 누적 방지.
const SEEN_MAX = 200;
const SEEN_TTL_MS = 10 * 60 * 1000;
const seenIds: Map<string, number> = new Map();  // id → ts (insertion order)

function rememberId(id: string, ts: number): void {
  // 10분 너머 기존 entry 제거 (가장 오래된 부터 — Map 의 insertion order 활용)
  const cutoff = ts - SEEN_TTL_MS;
  for (const [k, t] of seenIds) {
    if (t < cutoff) seenIds.delete(k);
    else break;  // insertion order — 첫 valid 발견 시 stop
  }
  // 새 id 추가
  seenIds.set(id, ts);
  // SEEN_MAX 초과 시 가장 오래된 (FIFO) 제거
  while (seenIds.size > SEEN_MAX) {
    const oldest = seenIds.keys().next().value;
    if (oldest === undefined) break;
    seenIds.delete(oldest);
  }
}

function hasSeen(id: string): boolean {
  return seenIds.has(id);
}

// test 전용 reset
export function _resetSeenIdsForTests(): void {
  seenIds.clear();
}

function nextId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function broadcast(msg: BroadcastMsg): void {
  const envelope: BroadcastEnvelope = { id: nextId(), msg, ts: Date.now() };
  rememberId(envelope.id, envelope.ts);
  // 1) BroadcastChannel 전송
  channel?.postMessage(envelope);
  // 2) Cycle 22 — storage event fallback. localStorage 변경은 다른 탭의 storage 리스너 trigger.
  // 같은 탭은 trigger 안 됨 → 본 탭 dedupe 필요 없음. 단 storage 에 envelope 시그너처 남김.
  if (isBrowser) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
      // 즉시 삭제하지 않음 — 마지막 envelope 보존 (다른 탭 첫 mount 시 빠른 sync 가능. 양은 작음).
    } catch {
      // private mode 등 localStorage 불가 — 무시
    }
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      authStatus: 'pending',
      isAuthenticated: false,
      setSession: (user) => {
        set({ user, authStatus: 'authed', isAuthenticated: true });
        broadcast({ type: 'auth-probe-result', user });
      },
      setAuthStatus: (status) => {
        set({ authStatus: status, isAuthenticated: status === 'authed' });
      },
      clearSession: () => {
        set({ user: null, authStatus: 'anon', isAuthenticated: false });
        /**
         * 화면잠금도 함께 푼다 (2026-08-28).
         *
         * 잠금은 **세션에 딸린 것**이다 — 세션이 끝났으면 지킬 것도 없다. 그런데 잠긴 시각이
         * localStorage 에 남는 탓에, 잠금 화면에서 로그아웃된 사람이 **로그인에 성공한 뒤
         * 다시 잠금 화면을 만났다**(그리고 거기서 또 로그아웃되는 고리에 갇혔다).
         *
         * ★ 여기서 지워도 «F5 로는 안 풀린다» 는 성질은 그대로다 — 새로고침은 세션이 살아
         *   있으면 `clearSession` 을 부르지 않는다(probe 200 → setSession). 이 줄이 도는 때는
         *   이미 로그아웃된 순간뿐이고, 그때 사람이 만나는 것은 잠금이 아니라 로그인 화면이다.
         */
        useScreenLockStore.getState().unlock();
        /**
         * ★★★ **모듈 캐시도 함께 비운다** (2026-08-31 · 사용자 실측).
         *    「테스트팀장으로 로그인 후 로그아웃하고 테스트 사원으로 로그인하니 **이사 · 테스트**」
         *    — SPA 라 로그아웃해도 페이지가 다시 뜨지 않아, 「최초 1회」 모듈 변수에 담아 둔
         *    **앞 사람의 직위·소속·사번·연차**가 그대로 남았다. 이름만 새 사람 것이라 더 나빴다.
         *    무엇이 걸려 있는지는 `lib/session-caches.ts` 참조.
         */
        clearSessionCaches();
        broadcast({ type: 'session-cleared' });
      },
    }),
    {
      name: 'workchat:auth',
      // user 만 영속화 — 새로고침 시 user 이름/아바타 즉시 표시 (probe 결과로 검증).
      // 토큰은 cookie 가 SSOT — store 보유 0 (review CRIT-2 정책 강화).
      partialize: (s) => ({ user: s.user }),
      onRehydrateStorage: () => (state) => {
        // hydration 직후 authStatus 는 항상 'pending' — AuthGuard 의 probe 가 최종 결정.
        // (cookie 가 살아있으면 200, 만료면 401 → clearSession.)
        if (state) {
          state.authStatus = 'pending';
          state.isAuthenticated = false;
        }
      },
    },
  ),
);

// `tokens-refreshed-ok` 외부 listener registry — 다른 모듈 (api/client 등) 이 자체
// BroadcastChannel 인스턴스를 만들지 않고 본 registry 에 구독. 채널 책임 단일화 (math-
// architect ISO-1/COUP-1 봉합 — 메시지 페이로드 evolution drift 차단).
type Unsubscribe = () => void;
const tokensRefreshedListeners = new Set<() => void>();

export function onTokensRefreshedOk(cb: () => void): Unsubscribe {
  tokensRefreshedListeners.add(cb);
  return () => { tokensRefreshedListeners.delete(cb); };
}

/**
 * 다른 탭의 broadcast 수신 → 본 탭 store 동기화. set() 직접 호출 (broadcast 재발 방지).
 *
 * `auth-probe-result`: 다른 탭이 probe 를 끝냈으면 본 탭은 자체 probe 생략 가능 (cold-start
 * 폭주 봉합). AuthGuard 가 본 store 변화를 감지해 probe 스킵.
 *
 * Cycle 22 — handleEnvelope 통합 + user mismatch reload.
 */
function handleEnvelope(envelope: BroadcastEnvelope): void {
  // 중복 처리 차단 (BroadcastChannel + storage event 양쪽 수신 가능)
  if (hasSeen(envelope.id)) return;
  rememberId(envelope.id, envelope.ts);
  // 7일 너머 stale envelope 무시 (storage 영구 보존)
  if (Date.now() - envelope.ts > 7 * 24 * 60 * 60 * 1000) return;

  const msg = envelope.msg;
  if (msg.type === 'session-cleared') {
    useAuthStore.setState({ user: null, authStatus: 'anon', isAuthenticated: false });
    // ★ 다른 탭이 로그아웃한 것도 «세션 종료»다 — 이 탭의 모듈 캐시도 함께 비운다.
    //   안 비우면 이 탭에서 다음 사람이 로그인했을 때 앞 사람의 값이 남는다.
    clearSessionCaches();
  } else if (msg.type === 'auth-probe-result') {
    const current = useAuthStore.getState().user;
    // Cycle 22 — user.id mismatch (다른 user 로 login 한 탭) → force reload 로 UI 정합성 회복.
    // 본 시점에 stale UI 가 위험 (보안/권한 측면). 단 무한 reload 회피 — 한 번만.
    if (
      current != null && msg.user != null && current.id !== msg.user.id && isBrowser
    ) {
      // setState 먼저 (다음 reload 후 일관)
      useAuthStore.setState({
        user: msg.user,
        authStatus: 'authed',
        isAuthenticated: true,
      });
      try { window.location.reload(); } catch { /* test env reload 미지원 */ }
      return;
    }
    useAuthStore.setState({
      user: msg.user,
      authStatus: msg.user ? 'authed' : 'anon',
      isAuthenticated: msg.user != null,
    });
  } else if (msg.type === 'tokens-refreshed-ok') {
    // 외부 등록 listener 들에게 fan-out (api/client 의 redundant refresh skip window 등).
    for (const cb of tokensRefreshedListeners) {
      try { cb(); } catch { /* listener 예외가 다른 listener 차단 안 함 */ }
    }
  }
}

if (channel) {
  channel.onmessage = (ev: MessageEvent<BroadcastEnvelope>) => {
    handleEnvelope(ev.data);
  };
}

// Cycle 22 — storage event fallback. BroadcastChannel 미지원 브라우저 또는 추가 안전망.
if (isBrowser) {
  window.addEventListener('storage', (ev) => {
    if (ev.key !== STORAGE_KEY || !ev.newValue) return;
    try {
      const envelope = JSON.parse(ev.newValue) as BroadcastEnvelope;
      if (envelope && envelope.id && envelope.msg) handleEnvelope(envelope);
    } catch {
      // JSON.parse 실패 무시
    }
  });
}

/**
 * /auth/refresh 성공 시 cross-tab 알림. 본 함수는 api/client 의 refresh 흐름에서 호출.
 */
export function broadcastTokensRefreshed(): void {
  broadcast({ type: 'tokens-refreshed-ok' });
}

// ─── probe single-flight ─────────────────────────
//
// AuthGuard 가 mount 시 호출하는 /users/me probe. 다중 탭 mount + 같은 탭 내 여러 곳
// (StrictMode 의 effect 2회 실행) 에서 동시 호출되면 backend 폭주. module-level 변수로
// 단일 in-flight Promise 강제 — Pre-mortem S3 의 "동시 탭 mount 시 N회 폭주" 봉합.

let probeInFlight: Promise<void> | null = null;

export function getProbeInFlight(): Promise<void> | null {
  return probeInFlight;
}

export function setProbeInFlight(p: Promise<void> | null): void {
  probeInFlight = p;
}
